import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useReducedMotion } from "framer-motion";
import api, { gbp, fileUrl } from "@/lib/api";
import { compareCars, ZONES, STATUS_LABEL } from "@/lib/compareRules";
import { EXAMPLE_CARS } from "@/lib/compareExamples";
import { HologramStage, hasWebGL } from "@/components/compare/HologramStage";
import { SvgCar } from "@/components/compare/SvgCar";
import { usePhotoHologram, useChipFade, PartChips } from "@/components/compare/PhotoStage";
import { Reveal } from "@/components/Motion";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Loader2, RotateCcw, ChevronDown, ShieldCheck } from "lucide-react";

const CHIP = { good: "bg-[#D1FAE5] text-[#065F46]", amber: "bg-[#FEF3C7] text-[#92400E]", red: "bg-[#FEE2E2] text-[#B91C1C]", grey: "bg-slate-100 text-slate-500" };
const NAME = (car) => `${car.year} ${car.make} ${car.model}`;
const photoOf = (c) => c.photo_url || (c.photos?.[0] ? fileUrl(c.photos[0]) : null);

function PhotoHolo({ photos, view, replay, reduce, onFail, result, selected, onSelect }) {
  const { host, show } = usePhotoHologram({ photos, view, replay, reduce, onFail });
  const chipProps = { result, view, selected, onSelect, show };
  return (
    <>
      <div ref={host} className="absolute inset-0" data-testid="photo-hologram" />
      <div className="hidden min-[760px]:flex absolute left-3 top-14 bottom-12 w-52 flex-col justify-center gap-2 z-10"><PartChips side="left" {...chipProps} /></div>
      <div className="hidden min-[760px]:flex absolute right-3 top-14 bottom-12 w-52 flex-col justify-center gap-2 z-10"><PartChips side="right" {...chipProps} /></div>
    </>
  );
}

function MobileChips({ result, view, selected, onSelect, replay, reduce }) {
  const show = useChipFade(replay, reduce);
  return (
    <div className="min-[760px]:hidden grid grid-cols-2 gap-2 mt-3 p-3 rounded-2xl bg-[#07121c]" data-testid="mobile-part-chips">
      <PartChips side="all" result={result} view={view} selected={selected} onSelect={onSelect} show={show} />
    </div>
  );
}
const Chip = ({ s, testId }) => <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full ${CHIP[s]}`} data-testid={testId}>{STATUS_LABEL[s]}</span>;

function useCars(params) {
  const [cars, setCars] = useState(null);
  useEffect(() => {
    if (params.get("example")) { setCars(EXAMPLE_CARS); return; }
    const ids = [params.get("a"), params.get("b")];
    if (!ids[0] || !ids[1]) { setCars(false); return; }
    Promise.all(ids.map((id) => api.get(`/listings/${id}`).then((r) => r.data))).then(setCars).catch(() => setCars(false));
  }, [params]);
  return cars;
}

function ScoreCard({ car, idx, result }) {
  const n = result.notes[idx];
  const best = result.better === idx;
  return (
    <div className={`bg-white rounded-3xl border p-6 ${best ? "border-teal" : "border-navy/10"}`} data-testid={`score-card-${idx === 0 ? "a" : "b"}`}>
      <div className="flex items-start justify-between gap-3">
        <div><span className={`text-xs font-bold uppercase tracking-widest ${idx === 0 ? "text-cyan-600" : "text-violet-600"}`}>Car {idx === 0 ? "A" : "B"}</span>
          <h3 className="font-heading font-bold text-xl text-navy mt-1">{NAME(car)}</h3></div>
        <div className="text-right"><div className="font-heading font-extrabold text-4xl text-navy tracking-tight" data-testid={`score-${idx === 0 ? "a" : "b"}`}>{result.scores[idx] ?? "—"}</div><div className="text-xs text-slatev">out of 100</div></div>
      </div>
      {best && <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold bg-teal text-white px-3 py-1 rounded-full" data-testid="lower-risk-badge"><ShieldCheck className="w-3.5 h-3.5" />Lower risk overall</span>}
      <div className="grid sm:grid-cols-2 gap-4 mt-5 text-sm">
        <div><p className="font-semibold text-navy mb-2">Positives</p>{n.positives.length ? n.positives.map((p) => <p key={p} className="text-[#047857]">• {p}</p>) : <p className="text-slatev">Nothing stronger than the other car.</p>}</div>
        <div><p className="font-semibold text-navy mb-2">Watch out for</p>{n.watch.length ? n.watch.map((p) => <p key={p} className="text-amber-700">• {p}</p>) : <p className="text-slatev">Nothing flagged.</p>}</div>
      </div>
    </div>
  );
}

function PartTable({ result, onPick, selected }) {
  return (
    <div className="bg-white rounded-3xl border border-navy/10 overflow-x-auto" data-testid="part-table">
      <table className="w-full text-sm min-w-[560px]">
        <thead><tr className="text-left text-slatev border-b border-navy/10"><th className="p-4">Part</th><th className="p-4">Car A</th><th className="p-4">Car B</th><th className="p-4">Why</th></tr></thead>
        <tbody>{ZONES.map((z) => (
          <tr key={z.key} onClick={() => z.physical && onPick(z.key)} data-testid={`part-row-${z.key}`}
            className={`border-b border-navy/5 last:border-0 ${z.physical ? "cursor-pointer hover:bg-teal-tint/50" : ""} ${selected === z.key ? "bg-teal-tint" : ""}`}>
            <td className="p-4 font-medium text-navy">{z.label}</td>
            <td className="p-4"><Chip s={result.zones[z.key].status[0]} testId={`chip-a-${z.key}`} /></td>
            <td className="p-4"><Chip s={result.zones[z.key].status[1]} testId={`chip-b-${z.key}`} /></td>
            <td className="p-4 text-slatev">{result.zones[z.key].reason}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function Specs({ cars }) {
  const rows = [["Price", (c) => gbp(c.price)], ["Year", (c) => c.year], ["Mileage", (c) => `${Number(c.mileage).toLocaleString("en-GB")} mi`], ["Fuel", (c) => c.fuel], ["Gearbox", (c) => c.transmission], ["Engine", (c) => (c.engine_cc ? `${c.engine_cc} cc` : null)], ["Condition", (c) => c.condition], ["Location", (c) => c.location]];
  return (
    <div className="bg-white rounded-3xl border border-navy/10 overflow-hidden" data-testid="specs-table">
      {rows.map(([label, f]) => (
        <div key={label} className="grid grid-cols-3 px-5 py-3 border-b border-navy/5 last:border-0 text-sm">
          <span className="text-slatev">{label}</span>{cars.map((c, i) => <span key={i} className="font-medium text-navy">{f(c) || "—"}</span>)}
        </div>
      ))}
    </div>
  );
}

const RULES = [
  "Engine and gearbox: higher risk on the car with 25,000+ more miles or 3+ years older; worth checking from 10,000 miles or 1 year.",
  "Tyres, brakes and suspension: from the latest MOT. Advisory or minor items are worth checking, anything more serious is higher risk. No MOT data shows as No data.",
  "Bodywork: seller condition (Excellent, Good, Fair, Needs work). Two or more steps worse is higher risk, one step is worth checking. MOT corrosion, rust or sill items are higher risk.",
  "Running costs: electric beats non-electric; otherwise an engine 800 cc+ bigger is worth checking.",
  "Price: the dearer car is higher risk if 15%+ more, worth checking if 5%+ more, unless it is both newer and lower mileage.",
  "Mileage history: higher risk if MOT mileage ever goes down or the listing is below the last MOT reading; worth checking above 40,000 miles a year.",
  "Score out of 100: weighted average (engine 3, brakes 2, suspension 2, bodywork 2, price 2, mileage history 2, tyres 1, running costs 1), ignoring parts with no data.",
];

export default function Compare() {
  const [params] = useSearchParams();
  const cars = useCars(params);
  const reduce = !!useReducedMotion();
  const [view, setView] = useState(0);
  const [selected, setSelected] = useState(null);
  const [replay, setReplay] = useState(0);
  const [gl, setGl] = useState(hasWebGL);
  const [photoFailed, setPhotoFailed] = useState(false);
  const onPhotoFail = useCallback(() => setPhotoFailed(true), []);
  const photos = useMemo(() => (cars ? { a: photoOf(cars[0]), b: photoOf(cars[1]) } : null), [cars]);
  const usePhoto = !!(photos?.a && photos?.b) && !photoFailed;
  const result = useMemo(() => (cars ? compareCars(cars[0], cars[1]) : null), [cars]);
  const onSelect = useCallback((z) => setSelected(z), []);
  const onFail = useCallback(() => setGl(false), []);

  if (cars === null) return <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-teal" /></div>;
  if (cars === false) return (
    <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="compare-missing">
      <h1 className="font-heading font-extrabold text-3xl text-navy">Pick two cars to compare</h1>
      <p className="text-slatev mt-3">Tap Compare + on any car, or try the example.</p>
      <Link to="/compare?example=1" className="mt-6 inline-flex h-11 px-6 items-center rounded-full bg-teal text-white font-semibold" data-testid="compare-try-example">See an example</Link>
    </div>
  );
  const zone = selected && result.zones[selected];
  const zLabel = selected && ZONES.find((z) => z.key === selected).label;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12" data-testid="compare-page">
      <Reveal>
        <span className="text-xs uppercase tracking-widest font-semibold text-teal">Hologram compare</span>
        <h1 className="font-heading font-extrabold text-navy tracking-tighter text-4xl sm:text-5xl lg:text-6xl mt-2">Two cars. One clear picture.</h1>
        <p className="text-slatev mt-4"><span className="text-cyan-700 font-semibold">A: {NAME(cars[0])}</span> · <span className="text-violet-700 font-semibold">B: {NAME(cars[1])}</span></p>
      </Reveal>

      <div className={`mt-8 relative rounded-3xl overflow-hidden bg-[#07121c] border border-cyan-400/20 ${usePhoto ? "h-[390px] min-[760px]:h-[520px]" : "h-[340px] sm:h-[460px]"}`} data-testid="hologram-stage">
        {usePhoto ? <PhotoHolo photos={photos} view={view} replay={replay} reduce={reduce} onFail={onPhotoFail} result={result} selected={selected} onSelect={onSelect} />
          : gl ? <HologramStage zones={result.zones} view={view} selected={selected} onSelect={onSelect} replayKey={replay} reduce={reduce} onFail={onFail} />
          : <SvgCar zones={result.zones} view={view} selected={selected} onSelect={onSelect} />}
        <div className="pointer-events-none absolute inset-0 bg-[repeating-linear-gradient(0deg,rgba(34,211,238,0.05)_0px,rgba(34,211,238,0.05)_1px,transparent_1px,transparent_4px)]" />
        <div className="absolute top-3 left-3 flex rounded-full bg-white/10 backdrop-blur p-1 text-xs font-semibold">
          {["Car A's view", "Car B's view"].map((t, i) => (
            <button key={t} onClick={() => setView(i)} data-testid={`view-${i === 0 ? "a" : "b"}`} className={`px-3 py-1.5 rounded-full transition-colors ${view === i ? (i === 0 ? "bg-cyan-400 text-[#06202A]" : "bg-violet-400 text-[#1b1035]") : "text-white/70"}`}>{t}</button>
          ))}
        </div>
        <button onClick={() => { setSelected(null); setReplay((n) => n + 1); }} data-testid="replay-merge" className="absolute top-3 right-3 h-8 px-3 rounded-full bg-white/10 backdrop-blur text-white text-xs font-semibold inline-flex items-center gap-1.5 hover:bg-white/20"><RotateCcw className="w-3.5 h-3.5" />Replay merge</button>
        <div className="absolute bottom-3 left-3 right-3 flex flex-wrap gap-3 text-[11px] text-white/70">
          {["good", "amber", "red", "grey"].map((s) => <span key={s} className="inline-flex items-center gap-1.5"><span className={`w-2.5 h-2.5 rounded-full ${s === "good" ? "bg-[#34D399]" : s === "amber" ? "bg-amber-400" : s === "red" ? "bg-red-400" : "bg-slate-500"}`} />{STATUS_LABEL[s]}</span>)}
          <span className="ml-auto">Tap a part for details</span>
        </div>
      </div>
      {usePhoto && <MobileChips result={result} view={view} selected={selected} onSelect={onSelect} replay={replay} reduce={reduce} />}
      {params.get("example") && <p className="mt-2 text-xs text-slatev" data-testid="example-photo-credit">Example photos: Thomas Doerfer and Vauxford, CC BY-SA 4.0, via Wikimedia Commons.</p>}

      <div className="mt-4 bg-white rounded-2xl border border-navy/10 p-5 min-h-[88px]" data-testid="zone-detail">
        {zone ? (
          <>
            <div className="flex items-center gap-3 flex-wrap"><h3 className="font-heading font-bold text-navy">{zLabel}</h3><Chip s={zone.status[view]} testId="zone-detail-chip" /><span className="text-xs text-slatev">{view === 0 ? "Car A" : "Car B"}'s view</span></div>
            <p className="text-sm text-navy mt-2">{zone.reason}</p>
            {zone.evidence.length > 0 && <ul className="mt-2 text-xs text-slatev space-y-1">{zone.evidence.map((e) => <li key={e}>• {e}</li>)}</ul>}
          </>
        ) : <p className="text-sm text-slatev">Tap a part of the hologram, or a row in the table below, to see why it's coloured that way.</p>}
      </div>

      <div className="grid md:grid-cols-2 gap-5 mt-10">{cars.map((c, i) => <ScoreCard key={i} car={c} idx={i} result={result} />)}</div>
      <h2 className="font-heading font-extrabold text-navy tracking-tight text-2xl sm:text-3xl mt-14 mb-5">Part by part</h2>
      <PartTable result={result} onPick={(k) => { setSelected(k); window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" }); }} selected={selected} />
      <h2 className="font-heading font-extrabold text-navy tracking-tight text-2xl sm:text-3xl mt-14 mb-5">Specs side by side</h2>
      <div className="grid grid-cols-3 px-5 text-xs font-bold uppercase tracking-widest text-slatev mb-2"><span /><span className="text-cyan-700">Car A</span><span className="text-violet-700">Car B</span></div>
      <Specs cars={cars} />
      <Collapsible className="mt-10 bg-white rounded-3xl border border-navy/10" data-testid="how-vorra-decides">
        <CollapsibleTrigger className="w-full flex items-center justify-between p-5 font-heading font-bold text-navy" data-testid="how-vorra-decides-toggle">How Vorra decides <ChevronDown className="w-4 h-4" /></CollapsibleTrigger>
        <CollapsibleContent className="px-5 pb-5 text-sm text-slatev space-y-2">
          {RULES.map((r) => <p key={r}>• {r}</p>)}
          <p className="pt-2 text-navy font-medium" data-testid="compare-disclaimer">Flags compare the two cars using the details listed. They aren't a mechanical inspection. Always view the car and check its paperwork before you buy.</p>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
